/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useMemo, useState } from 'react';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import {
  Button,
  Card,
  Modal,
  DatePicker,
  message,
  Divider,
  Typography,
} from 'antd';
import { useIntl } from 'react-intl';
import moment, { Moment } from 'moment';
import { TenderRegistrationVM } from '~src/types/db';
import { queryCache, useMutation } from 'react-query';
import {
  patchTendersRegistrationInfo,
  changeTenderState,
} from '~src/services/api';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';

const { Text } = Typography;

interface OffersLeftPanelProps {
  offerState?: 1 | 2;
  onOfferStateChange?: (s: 1 | 2) => void;
  tenderRegData?: TenderRegistrationVM | undefined;
  projectId: number;
  tenderId: number;
}

export const OffersLeftPanel: React.FC<OffersLeftPanelProps> = ({
  offerState = 1,
  onOfferStateChange,
  tenderRegData,
  projectId,
  tenderId,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFnLeft);

  const [state, setState] = useState<1 | 2>(offerState);
  const [isExtendOpen, setExtendOpen] = useState(false);

  // keep state in sync with parent prop
  useEffect(() => setState(offerState), [offerState]);

  // toggle helper
  const toggleState = () => {
    setState((prev) => {
      const next = prev === 1 ? 2 : 1;
      onOfferStateChange?.(next);
      return next;
    });
  };

  // console.log(`tenderRegData`, tenderRegData);

  const isBeforeDeadline = useMemo(() => {
    const deadlineAt = (tenderRegData as any)?.deadline_at;

    // after deadline OR if deadline_at is null
    if (!deadlineAt) return false;

    // deadline_at is unix seconds
    return moment.unix(deadlineAt).isAfter(moment());
  }, [tenderRegData]);

  // --- CLOSE tender mutation
  const [mutateTenderState, { isLoading: isChangingState }] = useMutation(
    changeTenderState,
    {
      onSuccess: () => {
        queryCache.invalidateQueries([QueryKeys.tenderAdminSharedTender]);
        queryCache.invalidateQueries([QueryKeys.tenderRegistration]);
        message.success(intl.formatMessage({ id: 'success' }));
      },
      onError: () => {
        message.error(intl.formatMessage({ id: 'error' }));
      },
    }
  );

  const handleCloseWithConfirm = () => {
    const firstConfirm = Modal.confirm({
      title: intl.formatMessage({ id: 'tenderClose-id-confirm-head' }),
      content: intl.formatMessage({ id: 'tenderClose-id-confirm-content' }),
      okText: intl.formatMessage({ id: 'tenderClose-id-confirm-yes' }),
      cancelText: intl.formatMessage({ id: 'tenderClose-id-confirm-no' }),
      okButtonProps: { danger: true, disabled: isChangingState },
      cancelButtonProps: { disabled: isChangingState },
      onOk: () => {
        firstConfirm.destroy();

        Modal.confirm({
          title: intl.formatMessage({
            id: 'tenderClose-id-confirm2-head',
          }),
          content: intl.formatMessage({
            id: 'tenderClose-id-confirm2-content',
          }),
          okText: intl.formatMessage({
            id: 'tenderClose-id-confirm2-yes',
          }),
          cancelText: intl.formatMessage({
            id: 'tenderClose-id-confirm2-no',
          }),
          okButtonProps: { danger: true, disabled: isChangingState },
          cancelButtonProps: { disabled: isChangingState },
          onOk: async () => {
            // Close Tender
            await mutateTenderState({ projectId, tenderId, action: 'close' });
          },
        });
      },
    });

    return firstConfirm;
  };

  return (
    <div css={styles.wrapper}>
      {/* Header */}
      <div css={styles.headerWrap}>
        <h2>{intl.formatMessage({ id: 'offers-header' })}</h2>
        <div css={styles.headerSub}>
          {intl.formatMessage({ id: 'tenderClose-id-main-content' })}
        </div>
      </div>

      {/* Info blocks */}
      <Card css={styles.featureCard} bordered>
        <div css={styles.featurePoint}>
          <div css={styles.pointTitle}>
            {intl.formatMessage({
              id: isBeforeDeadline
                ? 'tenderClose-id-beforeDeadline-head'
                : 'tenderClose-id-afterDeadline-head',
            })}
          </div>

          <div css={styles.pointText}>
            {intl.formatMessage({
              id: isBeforeDeadline
                ? 'tenderClose-id-beforeDeadline-content'
                : 'tenderClose-id-afterDeadline-content',
            })}
          </div>
        </div>
      </Card>

      {/* Actions */}
      <Card css={styles.actionsCard} bordered>
        <div css={styles.actionsGrid}>
          {/* CLOSE & VIEW */}
          <div css={styles.actionBlock}>
            <Button
              type="primary"
              block
              onClick={handleCloseWithConfirm}
              loading={isChangingState}>
              {intl.formatMessage({
                id: 'tenderClose-id-closeButton-label',
              })}
            </Button>
            <div css={styles.actionDesc}>
              {intl.formatMessage({
                id: 'tenderClose-id-closeButton-content',
              })}
            </div>
          </div>

          {/* EXTEND DEADLINE */}
          <div css={styles.actionBlock}>
            <Button block onClick={() => setExtendOpen(true)}>
              {intl.formatMessage({
                id: 'tenderClose-id-extendButton-label',
              })}
            </Button>
            <div css={styles.actionDesc}>
              {intl.formatMessage({
                id: 'tenderClose-id-extendButton-content',
              })}
            </div>
          </div>
        </div>
      </Card>

      {/* Modal extend */}
      <ExtendDeadlineModal
        open={isExtendOpen}
        onClose={() => setExtendOpen(false)}
        oldDeadlineAt={tenderRegData?.deadline_at}
        projectId={projectId}
        tenderId={tenderId}
      />
    </div>
  );
};

/**
 * Inner modal component that extend-deadline.
 */
const ExtendDeadlineModal: React.FC<{
  open: boolean;
  onClose: () => void;
  oldDeadlineAt?: number | null;
  projectId: number;
  tenderId: number;
}> = ({ open, onClose, oldDeadlineAt, projectId, tenderId }) => {
  const intl = useIntl();
  const styles = useStyles(styleFnLeft);

  const [extendDate, setExtendDate] = useState<Moment | null>(null);

  useEffect(() => {
    if (open) setExtendDate(null);
  }, [open]);

  const oldDeadlineText = useMemo(() => {
    if (!oldDeadlineAt) return null;
    const formatted = moment.unix(oldDeadlineAt).format('YYYY-MM-DD HH:mm');
    return intl.formatMessage(
      { id: 'tenderClose-id-extendModal-oldDeadlineLabel' },
      { date: formatted }
    );
  }, [oldDeadlineAt, intl]);

  // --- Logic Helpers ---

  const handleAddDays = (days: number) => {
    const baseDate = extendDate
      ? moment(extendDate)
      : oldDeadlineAt
      ? moment.unix(oldDeadlineAt)
      : moment();
    setExtendDate(baseDate.clone().add(days, 'days'));
  };

  const handleSetTime = (hour: number, minute: number) => {
    const baseDate = extendDate
      ? moment(extendDate)
      : oldDeadlineAt
      ? moment.unix(oldDeadlineAt)
      : moment();
    const newDate = baseDate
      .clone()
      .set({ hour, minute, second: 0, millisecond: 0 });

    if (newDate.isBefore(moment())) {
      newDate.add(1, 'day');
    }
    setExtendDate(newDate);
  };

  // --- Constraints ---
  const disabledDate = (current?: Moment) =>
    !!current && current < moment().startOf('day');

  const disabledTime = (date?: Moment) => {
    const current = date ?? moment();
    const now = moment();

    if (current.isAfter(now, 'day')) {
      return { disabledHours: () => [], disabledMinutes: () => [] };
    }

    if (current.isSame(now, 'day')) {
      const hours: number[] = [];
      for (let h = 0; h < now.hour(); h++) hours.push(h);

      const minutes = (selectedHour: number) => {
        if (selectedHour !== now.hour()) return [];
        const mins: number[] = [];
        for (let m = 0; m < now.minute(); m++) mins.push(m);
        return mins;
      };

      return { disabledHours: () => hours, disabledMinutes: minutes };
    }

    return {
      disabledHours: () => Array.from({ length: 24 }, (_, h) => h),
      disabledMinutes: () => Array.from({ length: 60 }, (_, m) => m),
    };
  };

  const isValid = useMemo(() => !!extendDate && extendDate.isAfter(moment()), [
    extendDate,
  ]);

  const [
    doPatchDeadlineMutation,
    { isLoading: isPatchingDeadline },
  ] = useMutation(
    (deadlineAt: number) =>
      patchTendersRegistrationInfo({
        projectId,
        tenderId,
        deadline_at: deadlineAt,
      }),
    {
      onSuccess: () => {
        queryCache.invalidateQueries([
          QueryKeys.tenderRegistration,
          {
            projectId: String(projectId),
            tender_id: String(tenderId),
          },
        ]);

        message.success(intl.formatMessage({ id: 'extend-deadline-success' }));
        onClose();
      },
      onError: () => {
        message.error(intl.formatMessage({ id: 'extend-deadline-failed' }));
      },
    }
  );

  const handleOk = async () => {
    if (!extendDate || !isValid) return;

    const deadlineAtUnix = extendDate.unix(); // unix seconds

    await doPatchDeadlineMutation(deadlineAtUnix);
  };

  return (
    <Modal
      visible={open}
      onCancel={isPatchingDeadline ? undefined : onClose}
      title={intl.formatMessage({
        id: 'tenderClose-id-extendButton-label',
      })}
      okText={intl.formatMessage({
        id: 'tenderClose-id-extendDeadline-confirm-yes',
      })}
      cancelText={intl.formatMessage({ id: 'tenderClose-id-confirm-no' })}
      okButtonProps={{ disabled: !isValid }}
      confirmLoading={isPatchingDeadline}
      maskClosable={!isPatchingDeadline}
      closable={!isPatchingDeadline}
      onOk={handleOk}
      destroyOnClose
      width={420}>
      <div css={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ color: '#666' }}>
          {intl.formatMessage({
            id: 'tenderClose-id-afterDeadline-content',
          })}
        </div>

        {/* Date Picker */}
        <div css={styles.inputGroup}>
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {intl.formatMessage({
                id: 'tenderClose-id-extendModal-newDeadlineLabel',
              })}
            </Text>

            {!!oldDeadlineText && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {oldDeadlineText}
              </Text>
            )}
          </div>

          <DatePicker
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            style={{ width: '100%' }}
            value={extendDate as any}
            onChange={(val) => setExtendDate(val)}
            disabledDate={disabledDate}
            // @ts-ignore
            disabledTime={disabledTime}
            placeholder={intl.formatMessage({
              id: 'tenderClose-id-extendModal-datePlaceholder',
            })}
            getPopupContainer={(trigger) =>
              trigger.parentElement || document.body
            }
          />
        </div>

        <Divider style={{ margin: '4px 0' }} />

        {/* Controls Grid */}
        <div css={styles.controlsGrid}>
          {/* Left Column: Add Days */}
          <div css={styles.controlColumn}>
            <Text strong style={{ fontSize: 12 }}>
              {intl.formatMessage({
                id: 'tenderClose-id-extendModal-addDaysTitle',
              })}
            </Text>
            <Button onClick={() => handleAddDays(1)}>
              {intl.formatMessage({ id: 'tenderClose-id-extendModal-add1Day' })}
            </Button>
            <Button onClick={() => handleAddDays(7)}>
              {intl.formatMessage({
                id: 'tenderClose-id-extendModal-add7Days',
              })}
            </Button>
            <Button onClick={() => handleAddDays(14)}>
              {intl.formatMessage({
                id: 'tenderClose-id-extendModal-add14Days',
              })}
            </Button>
          </div>

          {/* Right Column: Select Time */}
          <div css={styles.controlColumn}>
            <Text strong style={{ fontSize: 12 }}>
              {intl.formatMessage({
                id: 'tenderClose-id-extendModal-selectTimeTitle',
              })}
            </Text>
            <div css={styles.buttonStack}>
              <Button onClick={() => handleSetTime(12, 0)}>12:00</Button>
              <Button onClick={() => handleSetTime(23, 59)}>23:59</Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const styleFnLeft: ThemedStyleFn = () => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  headerWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    paddingTop: 20,
    paddingLeft: 10,
    paddingBottom: 10,
    whiteSpace: 'pre-wrap',
  },
  headerSub: { color: '#6a6969', whiteSpace: 'pre-wrap' },

  featureCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  featurePoint: {
    background: '#fafafa',
    border: '1px solid #eee',
    borderRadius: 6,
    padding: '12px 14px',
  },
  pointTitle: { fontWeight: 700, marginBottom: 6, whiteSpace: 'pre-wrap' },
  pointText: {
    color: '#555',
    fontSize: 13,
    lineHeight: 1.4,
    whiteSpace: 'pre-wrap',
  },

  actionsCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    '@media (max-width: 992px)': { gridTemplateColumns: '1fr' },
  },
  actionBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    background: '#fafafa',
    border: '1px solid #eee',
    borderRadius: 6,
    padding: '12px 14px',
    whiteSpace: 'pre-wrap',
  },
  actionDesc: {
    color: '#666',
    fontSize: 12,
    lineHeight: 1.4,
    whiteSpace: 'pre-wrap',
  },

  // --- Modal Styles ---
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  controlsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
  },
  controlColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  buttonStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
});
